import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    Typography,
    Button,
    Stack,
    Avatar,
    TextField,
    Chip,
    Skeleton,
    InputAdornment,
    Badge
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Search, FilterList, Visibility } from '@mui/icons-material';
import { useAllOrders } from 'hooks/useOrder';
import { useDebounce } from 'hooks/useDebounce';
import FilterDrawer from 'components/common/FilterDrawer';

const statusOptions = ['pending', 'preparing', 'prepared', 'picked', 'delivered', 'canceled'];
const paymentTypes = ['wallet', 'card', 'cash'];
const orderTypes = ['delivery', 'pickup'];

// Helper for Status Styling
const getStatusConfig = (status) => {
    const configs = {
        pending: { color: 'warning', label: 'Pending' },
        preparing: { color: 'secondary', label: 'Preparing' },
        prepared: { color: 'info', label: 'Prepared' },
        picked: { color: 'primary', label: 'Picked Up' },
        delivered: { color: 'success', label: 'Delivered' },
        canceled: { color: 'error', label: 'Canceled' }
    };
    return configs[status] || { color: 'default', label: status };
};

const getPaymentStatusConfig = (paymentCompleted) => {
    return paymentCompleted ? { color: 'success', label: 'Paid' } : { color: 'error', label: 'Pending' };
};

export default function OrdersTable() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Filters
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [search, setSearch] = useState('');

    // Debounce search
    const debouncedSearch = useDebounce(search, 500);

    const {
        data: ordersData,
        isLoading
    } = useAllOrders({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        status: filters.status !== 'All' ? filters.status : undefined,
        paymentType: filters.paymentType !== 'All' ? filters.paymentType : undefined,
        orderType: filters.orderType !== 'All' ? filters.orderType : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy: filters.sortBy || 'createdAt',
        sortOrder: filters.sortOrder || 'desc'
    });

    const orders = ordersData?.data || [];
    const total = ordersData?.total || 0;

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, filters]);

    const handleChangePage = (_event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: statusOptions.map((opt) => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))
        },
        {
            name: 'paymentType',
            label: 'Payment Type',
            type: 'select',
            options: paymentTypes.map((opt) => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))
        },
        {
            name: 'orderType',
            label: 'Order Type',
            type: 'select',
            options: orderTypes.map((opt) => ({ label: opt.charAt(0).toUpperCase() + opt.slice(1), value: opt }))
        },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { label: 'Date Created', value: 'createdAt' },
                { label: 'Amount', value: 'amount' },
                { label: 'Status', value: 'status' }
            ]
        },
        {
            name: 'sortOrder',
            label: 'Sort Order',
            type: 'select',
            options: [
                { label: 'Descending', value: 'desc' },
                { label: 'Ascending', value: 'asc' }
            ]
        },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' }
    ];

    const activeFiltersCount = Object.keys(filters).length;

    return (
        <Paper elevation={0} sx={{ width: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            {/* Header & Filters */}
            <Box p={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
                    <TextField
                        placeholder="Search by Order Code or Name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ maxWidth: 400 }}
                    />

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge badgeContent={activeFiltersCount} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {activeFiltersCount > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />

            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Order Code</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Items</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Payment</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Skeleton width={80} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="circular" width={40} height={40} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={150} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton width={60} />
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="rounded" width={80} height={24} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Skeleton width={60} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                    <Typography color="text.secondary">No orders matching your criteria.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order) => {
                                const statusCfg = getStatusConfig(order.status);
                                const paymentStatusCfg = getPaymentStatusConfig(order.paymentCompleted);
                                return (
                                    <TableRow hover key={order.id}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} color="primary">
                                                #{order?.code}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(order.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
                                                    {order.user?.firstName?.[0]}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {order.user?.firstName} {order.user?.lastName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {order.products.map((p) => `${p.name} (x${p.quantity})`).join(', ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight={700}>₦{order.amount.toLocaleString()}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Chip
                                                    label={paymentStatusCfg.label}
                                                    color={paymentStatusCfg.color}
                                                    size="small"
                                                    sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                                />
                                                {order.paymentType && (
                                                    <Chip
                                                        label={order.paymentType}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }}
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={order.orderType === 'pickup' ? 'PICKUP' : statusCfg.label}
                                                color={statusCfg.color}
                                                size="small"
                                                sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                size="small"
                                                startIcon={<Visibility />}
                                                onClick={() => navigate(`/vendor/orders/${order.id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}
